import { Component, Element, Prop } from "@stencil/core";
import { isCookieConsentAccepted } from "../../utils/cookieConsent";

@Component({
    tag: "ux-iws-third-party-script",
    shadow: false,
})
export class ThirdPartyScript {
    @Element() element: HTMLUxIwsThirdPartyScriptElement;

    /**
     * The URL of the third-party script to be loaded.
     */
    @Prop() src: string;

    /**
     * The type of the script.
     */
    @Prop() type: string = "text/javascript";

    /**
     * The integrity attribute for Subresource Integrity (SRI).
     */
    @Prop() integrity?: string;

    /**
     * The cross-origin attribute for the script.
     */
    @Prop() crossOrigin?: "anonymous" | "use-credentials";

    /**
     * If true, the script will be deferred until the page has finished parsing.
     */
    @Prop() defer: boolean = false;

    /**
     * If true, the script will be executed asynchronously.
     */
    @Prop() async: boolean = false;

    /**
     * The ID of the script element.
     */
    @Prop() scriptId?: string;

    /**
     * nomodule attribute to prevent loading the script in browsers that support ES modules.
     */
    @Prop() noModule: boolean = false;

    /**
     * referrer policy for the script.
     */
    @Prop() referrerPolicy?: string;

    /**
     * comma-separated list of allowed locales for the script.
     * This is used to load the script only on specific locales.
     */
    @Prop() allowedLocales: string | null = null;

    /**
     * cookie consent required to load the script.
     */
    @Prop() cookieConsentRequired: boolean = false;

    async componentWillLoad() {
        const hasCookieConsent = await this.hasCookieConsent();
        if (hasCookieConsent && this.isLocaleAllowed() && this.src) {
            const script = this.buildScriptTag();
            this.insertScript(script);
        }
    }

    private async hasCookieConsent(): Promise<boolean> {
        if (!this.cookieConsentRequired) {
            return true;
        }
        const hasConsented = await isCookieConsentAccepted().then(
            () => true,
            () => false
        );
        return hasConsented;
    }

    private isLocaleAllowed(): boolean {
        if (this.allowedLocales === null) {
            return true; // No restrictions on locales
        }
        const detectedLocale = document.documentElement.lang || null;
        const allowedLocalesArray = this.allowedLocales
            .split(",")
            .map((locale) => locale.trim().toLowerCase());
        return allowedLocalesArray.includes(detectedLocale?.toLowerCase());
    }

    private buildScriptTag(): HTMLScriptElement {
        const script = document.createElement("script");
        script.src = this.src;
        script.type = this.type;
        if (this.integrity) {
            script.integrity = this.integrity;
        }
        if (this.crossOrigin) {
            script.crossOrigin = this.crossOrigin;
        }
        if (this.defer) {
            script.defer = true;
        }
        if (this.async) {
            script.async = true;
        }
        if (this.scriptId) {
            script.id = this.scriptId;
        }
        if (this.noModule) {
            script.setAttribute("nomodule", "");
        }
        if (this.referrerPolicy) {
            script.referrerPolicy = this.referrerPolicy;
        }
        return script;
    }

    private insertScript(script: HTMLScriptElement) {
        this.element.appendChild(script);
    }
}
